import { useState } from 'react';
import { useSectionsList, useCreateSection } from '../../../api/hooks';
import { PanelCard, Button, PagedFooter, Spinner } from '../../../components/ui';
import usePagination from '../../../lib/usePagination';
import { FaPlus } from '../../../components/ui/icons';

interface Props {
  gradeId: number;
  gradeName?: string;
  selectedId: number | null;
  onSelect: (id: number) => void;
}

export default function SectionListPanel({ gradeId, gradeName, selectedId, onSelect }: Props) {
  const q = useSectionsList(gradeId);
  const createMut = useCreateSection();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sections = q.data?.sections ?? [];
  const pg = usePagination(sections, 10, gradeId);

  async function submit() {
    setError(null);
    try {
      const created = await createMut.mutateAsync({ school_grade: gradeId, name: name.trim() });
      setName('');
      setCreating(false);
      onSelect(created.id);
    } catch (e: any) {
      setError(e?.response?.data?.name?.[0] ?? 'Could not create section.');
    }
  }

  return (
    <PanelCard
      title={gradeName ? `${gradeName} sections` : 'Sections'}
      className="self-start"
      actions={
        <Button
          variant="primary"
          size="sm"
          icon={<FaPlus size={18} strokeWidth={1.8} />}
          onClick={() => setCreating((v) => !v)}>
          New section
        </Button>
      }>
      <div className="space-y-2">
        {creating && (
          <div className="rounded-[16px] border-[1.5px] border-dashed border-switch-off bg-surface-2 p-3.5">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Section name (e.g. D)"
              className="min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3.5 text-sm text-ink placeholder:text-muted"
              aria-label="Section name"
            />
            {error && <p role="alert" className="mt-1.5 text-xs text-danger">{error}</p>}
            <div className="mt-2.5 flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="min-h-[38px]"
                onClick={() => { setCreating(false); setName(''); setError(null); }}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="min-h-[38px]"
                disabled={!name.trim()}
                loading={createMut.isPending}
                onClick={submit}>
                Create
              </Button>
            </div>
          </div>
        )}

        {q.isLoading && (
          <div className="flex justify-center py-4"><Spinner size="md" /></div>
        )}
        {pg.pageItems.map((s) => {
          const on = selectedId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={on}
              onClick={() => onSelect(s.id)}
              className={`flex w-full items-center gap-3 rounded-[16px] border p-3.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                on ? 'border-navy bg-surface ring-1 ring-navy' : 'border-line bg-surface hover:border-switch-off hover:bg-surface-2'}`}
            >
              <span aria-hidden="true" className={`grid h-10 w-10 flex-none place-items-center rounded-[12px] font-display text-[17px] font-bold ${
                on ? 'bg-accent text-navy' : 'bg-mist text-navy dark:text-ink'}`}>
                {s.name.slice(0, 2).toUpperCase()}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-ink">Section {s.name}</span>
                <span className="block truncate text-xs text-muted">
                  {s.homeroom_teacher
                    ? `Homeroom · ${s.homeroom_teacher.first_name} ${s.homeroom_teacher.last_name}`
                    : 'No homeroom teacher yet'}
                </span>
              </span>
              <span className="flex-none font-mono text-sm font-semibold text-ink">{s.student_count} students</span>
            </button>
          );
        })}

        {sections.length === 0 && !q.isLoading && (
          <p className="py-4 text-center text-xs text-muted">No sections yet.</p>
        )}
      </div>
      <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="mt-3" />
    </PanelCard>
  );
}
